import {
  Circle, CheckCircle2, CheckCheck, Target, PauseCircle, XCircle,
  Star, Flag, AlertTriangle, Ban, RotateCcw, Zap, Heart, ThumbsDown,
} from "lucide-react";

// ------ Icon Registry (used by OutcomesPage + dynamic meta) ------
export const ICON_MAP = {
  circle: Circle,
  "check-circle": CheckCircle2,
  "check-check": CheckCheck,
  target: Target,
  pause: PauseCircle,
  x: XCircle,
  star: Star,
  flag: Flag,
  alert: AlertTriangle,
  ban: Ban,
  rotate: RotateCcw,
  zap: Zap,
  heart: Heart,
  "thumbs-down": ThumbsDown,
};
export const ICON_OPTIONS = Object.keys(ICON_MAP);

// ------ Legacy hardcoded STATUS_META (default statuses, keeps consumers working) ------
export const STATUS_META = {
  da_svolgere: {
    label: "Da svolgere", icon: Circle,
    dot: "bg-blue-500", badge: "bg-blue-100 text-blue-800 border-blue-200",
    solidBadge: "bg-white text-blue-700 border border-blue-200",
    cardBg: "bg-white", cardText: "text-slate-900", ring: "",
    bar: "#3B82F6", strong: false, color: "#3B82F6",
  },
  effettuato: {
    label: "Effettuato", icon: CheckCircle2,
    dot: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    solidBadge: "bg-emerald-600 text-white border-emerald-700",
    cardBg: "bg-emerald-50", cardText: "text-emerald-950", ring: "ring-1 ring-emerald-300",
    bar: "#10B981", strong: true, color: "#10B981",
  },
  no_show: {
    label: "No-Show", icon: XCircle,
    dot: "bg-rose-500", badge: "bg-rose-100 text-rose-800 border-rose-200",
    solidBadge: "bg-rose-600 text-white border-rose-700",
    cardBg: "bg-rose-50", cardText: "text-rose-950", ring: "ring-1 ring-rose-300",
    bar: "#F43F5E", strong: true, color: "#F43F5E",
  },
  annullato: {
    label: "Annullato", icon: Ban,
    dot: "bg-slate-500", badge: "bg-slate-100 text-slate-700 border-slate-200",
    solidBadge: "bg-slate-600 text-white border-slate-700",
    cardBg: "bg-slate-100", cardText: "text-slate-900", ring: "ring-1 ring-slate-300",
    bar: "#64748B", strong: true, color: "#64748B",
  },
  riprogrammato: {
    label: "Riprogrammato", icon: RotateCcw,
    dot: "bg-amber-500", badge: "bg-amber-100 text-amber-800 border-amber-200",
    solidBadge: "bg-amber-600 text-white border-amber-700",
    cardBg: "bg-amber-50", cardText: "text-amber-950", ring: "ring-1 ring-amber-300",
    bar: "#F59E0B", strong: true, color: "#F59E0B",
  },
};

export const STATUS_LIST = ["da_svolgere", "effettuato", "no_show", "annullato", "riprogrammato"];
export const COMPLETED_STATUSES = new Set(["effettuato"]);

// ------ Dynamic meta (for OutcomesContext / custom outcomes) ------
export function fallbackMeta(key = "?") {
  return {
    key, label: key || "?", icon: Circle, color: "#94A3B8", bar: "#94A3B8",
    strong: false, is_completed: false, is_default: true,
    solidBadge: "bg-slate-500 text-white",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
    cardBg: "bg-white", cardText: "text-slate-900", ring: "",
    solidBadgeStyle: { backgroundColor: "#94A3B8", color: "#fff" },
    dotStyle: { backgroundColor: "#94A3B8" },
    cardBgStyle: { backgroundColor: "#94A3B814" },
  };
}

export function computeMeta(o) {
  if (!o) return fallbackMeta();
  const color = o.color || "#94A3B8";
  const legacy = STATUS_META[o.key] || {};
  return {
    key: o.key,
    label: o.label,
    icon: ICON_MAP[o.icon] || legacy.icon || Circle,
    iconKey: o.icon || "circle",
    color, bar: color,
    strong: !o.is_default,
    is_completed: !!o.is_completed,
    is_default: !!o.is_default,
    calendar: o.calendar,
    // Legacy class-based visuals (fallback to slate if unknown)
    dot: legacy.dot || "bg-slate-500",
    badge: legacy.badge || "bg-slate-100 text-slate-700 border-slate-200",
    solidBadge: legacy.solidBadge || "text-white",
    cardBg: legacy.cardBg || "bg-slate-50",
    cardText: legacy.cardText || "text-slate-900",
    ring: legacy.ring || "",
    // Dynamic inline styles (preferred for custom colors)
    solidBadgeStyle: { backgroundColor: color, color: "#fff", borderColor: color },
    lightBadgeStyle: { backgroundColor: `${color}22`, color, border: `1px solid ${color}55` },
    dotStyle: { backgroundColor: color },
    cardBgStyle: { backgroundColor: `${color}1F` },
    cardTintStyle: { backgroundColor: `${color}12` },
  };
}

export const ROLE_LABEL = {
  admin: "Admin",
  manager: "Manager",
  collaborator: "Consulente",
  trainer: "Trainer",
  receptionist: "Receptionist",
};

export const WEEKDAYS = [
  { key: "monday", label: "Lun" },
  { key: "tuesday", label: "Mar" },
  { key: "wednesday", label: "Mer" },
  { key: "thursday", label: "Gio" },
  { key: "friday", label: "Ven" },
  { key: "saturday", label: "Sab" },
  { key: "sunday", label: "Dom" },
];

export const WEEKDAY_INDEX = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export function pad(n) { return String(n).padStart(2, "0"); }

export function toLocalIso(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

export function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function fmtDateIt(d) {
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
}

export function fmtRange(start, end) {
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameMonth && sameYear) {
    return `${start.getDate()} – ${end.getDate()} ${start.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}`;
  }
  return `${start.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} – ${end.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}`;
}

function toMin(s) { const [h, m] = s.split(":").map(Number); return h * 60 + m; }

export function unionWorkingRanges(users, weekdayKey) {
  const ranges = [];
  users.forEach((u) => {
    const slots = (u.working_hours && u.working_hours[weekdayKey]) || [];
    slots.forEach((s) => ranges.push({ s: toMin(s.start), e: toMin(s.end) }));
  });
  if (!ranges.length) return [];
  ranges.sort((a, b) => a.s - b.s);
  const merged = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const last = merged[merged.length - 1];
    if (ranges[i].s <= last.e) last.e = Math.max(last.e, ranges[i].e);
    else merged.push(ranges[i]);
  }
  return merged;
}

export function computeNonWorkingBlocks(ranges, dayStartHour, dayEndHour) {
  const startMin = dayStartHour * 60;
  const endMin = dayEndHour * 60;
  if (!ranges.length) return [{ start: startMin, end: endMin }];
  const blocks = [];
  let cursor = startMin;
  for (const r of ranges) {
    const rs = Math.max(startMin, r.s);
    const re = Math.min(endMin, r.e);
    if (re <= startMin || rs >= endMin) continue;
    if (rs > cursor) blocks.push({ start: cursor, end: rs });
    cursor = Math.max(cursor, re);
  }
  if (cursor < endMin) blocks.push({ start: cursor, end: endMin });
  return blocks;
}
